import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F7F5F0',
    padding: '2rem',
  },
  header: {
    maxWidth: '920px',
    margin: '0 auto 2rem',
    padding: '2.5rem',
    borderRadius: '16px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 18px 60px rgba(17, 17, 16, 0.08)',
  },
  title: {
    margin: 0,
    fontFamily: 'Playfair Display, Georgia, serif',
    fontSize: '2.5rem',
    color: '#111110',
  },
  subtitle: {
    marginTop: '1rem',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '1rem',
    color: '#555553',
    lineHeight: 1.75,
  },
  filterBar: {
    maxWidth: '1180px',
    margin: '0 auto 1.5rem',
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: '1fr 1fr',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #E0DDD6',
    backgroundColor: '#FFFFFF',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#111110',
    outline: 'none',
  },
  section: {
    maxWidth: '1180px',
    margin: '0 auto',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1rem',
  },
  sectionTitle: {
    margin: 0,
    fontFamily: 'Playfair Display, Georgia, serif',
    fontSize: '1.75rem',
    color: '#111110',
  },
  viewAllLink: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    color: '#4A9E8A',
    textDecoration: 'none',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1.25rem',
  },
  loading: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#555553',
    textAlign: 'center',
    marginTop: '1rem',
  },
  error: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#E24B4A',
    textAlign: 'center',
    marginTop: '1rem',
  },
};

function ListingCard({ listing }) {
  const [hovered, setHovered] = useState(false);

  const thumbnailUrl =
    listing.images?.[listing.thumbnailIndex || 0]?.thumbnail ||
    listing.images?.[0]?.thumbnail ||
    listing.imageUrl ||
    'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&q=70';

  const priceLabel = `$${listing.price} / ${listing.priceType || 'night'}`;

  return (
    <Link to={`/listing/${listing.id}`} style={{ textDecoration: 'none' }}>
      <article
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
      >
        <div style={{ borderRadius: 12, overflow: 'hidden', aspectRatio: '1 / 1', position: 'relative' }}>
          <img
            src={thumbnailUrl}
            alt={listing.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <div style={{ position: 'absolute', inset: 0, background: hovered ? 'rgba(17,17,16,0.04)' : 'transparent' }} />
        </div>
        <div style={{ paddingTop: 10 }}>
          <p style={{ fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 13, fontWeight: 600, color: '#111110', lineHeight: 1.3, margin: 0 }}>
            {listing.title}
          </p>
          <p style={{ fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 12, color: '#888880', margin: '2px 0 4px', lineHeight: 1.3 }}>
            {listing.location}
          </p>
          <p style={{ fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 13, color: '#111110', margin: 0 }}>
            <strong>{priceLabel}</strong>
          </p>
        </div>
      </article>
    </Link>
  );
}

function Stays() {
  const [listings, setListings] = useState([]);
  const [filterLocation, setFilterLocation] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStays = async () => {
      setLoading(true);
      setError('');

      try {
        const listingsRef = collection(db, 'listings');
        const staysQuery = query(listingsRef, where('category', '==', 'stays'));
        const snapshot = await getDocs(staysQuery);
        const stays = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort(
            (a, b) =>
              (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
          );
        setListings(stays);
      } catch (fetchError) {
        console.error('Stays fetch error', fetchError);
        setError('Unable to load stays. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchStays();
  }, []);

  const normalizedLocation = filterLocation.trim().toLowerCase();
  const filteredListings = listings.filter((listing) => {
    const matchesLocation = normalizedLocation
      ? (listing.location || '').toLowerCase().includes(normalizedLocation)
      : true;
    const priceValue = Number(listing.price || 0);
    const matchesMin = minPrice ? priceValue >= Number(minPrice) : true;
    const matchesMax = maxPrice ? priceValue <= Number(maxPrice) : true;
    return matchesLocation && matchesMin && matchesMax;
  });

  return (
    <main style={styles.page}>
      <section style={styles.header}>
        <h1 style={styles.title}>Stays</h1>
        <p style={styles.subtitle}>
          Browse hostel rooms, private spaces, sublets and short-term stays from local hosts.
          Use filters to narrow down your search by location and price.
        </p>
      </section>

      <section style={styles.filterBar}>
        <input
          style={styles.input}
          type="text"
          placeholder="Search by city, neighborhood, or listing"
          value={filterLocation}
          onChange={(e) => setFilterLocation(e.target.value)}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <input
            style={styles.input}
            type="number"
            placeholder="Min price"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            min="0"
          />
          <input
            style={styles.input}
            type="number"
            placeholder="Max price"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            min="0"
          />
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Available stays</h2>
          <Link to="/post" style={styles.viewAllLink}>
            Post a stay
          </Link>
        </div>

        {loading ? (
          <p style={styles.loading}>Loading listings…</p>
        ) : error ? (
          <p style={styles.error}>{error}</p>
        ) : filteredListings.length > 0 ? (
          <div style={styles.grid}>
            {filteredListings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        ) : (
          <p style={styles.loading}>No stays match your filters.</p>
        )}
      </section>
    </main>
  );
}

export default Stays;